import moment from 'moment';
import 'moment/locale/es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import SvgIcon from '../../utils/Icon';
import { ReactComponent as Teleconsulta } from '../../assets/icons/agendar.svg';
import InputFile from '../../utils/InputFile';
import { useState } from 'react';
import TableDocuments from '../../components/ClinicComponent/TableDocuments';
import { ReactComponent as Archivo } from '../../assets/icons/archivo.svg';
import { alertaGeneral } from '../../utils/alertas';
import { formatServiceName } from '../../utils/formatServiceName';

const CardCitasControl = ({ allAppointments, handleVideoCall, handleCancel, isPatient = true }) => {
    moment.locale('es');

    const ordenEstados = {
        "Agendada": 1,
        "Cancelada": 2,
        "Finalizada": 3
    }

    const colorEstado = (estado) => {
        switch (estado) {
            case 'Agendada':
                return 'bg-[#0aad5cff]';
            case 'Cancelada':
                return 'bg-[#70706eff]';
            case 'Finalizada':
                return 'bg-[#205087ff]';
        }
    }

    // Estado global para saber en qué cita se está subiendo archivo
    const [appointmentIdForUpload, setAppointmentIdForUpload] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalAppointmentId, setModalAppointmentId] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);

    // Abrir modal y cargar documentos
    const handleOpenModal = async (appointmentId) => {
        setModalAppointmentId(appointmentId);
        setModalOpen(true);
        setSelectedFile(null);
        // Lógica para cargar documentos ya subidos
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/doctor/getDocumentsAppointment/${appointmentId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            // Normalizar a formato esperado por TableDocuments
            const docs = (data.documents || []).map((url, i) => ({
                name: `Documento ${i + 1}`,
                url,
                nameToSave: `doc${i}`,
                isEdit: false
            }));
            setDocuments(docs);
        } catch (e) {
            setDocuments([]);
        }
    };

    // Subir archivo SOLO al presionar Enviar
    const handleSendFile = async () => {
        if (!selectedFile) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('appointmentId', modalAppointmentId);
        formData.append('file', selectedFile);
        const token = localStorage.getItem('token');
        try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/consentimientoInformado`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
            });
            setSelectedFile(null);
            // Recargar documentos
            await handleOpenModal(modalAppointmentId);
            alertaGeneral({
                titulo: 'Documento enviado correctamente',
                messageHtml: '',
            });
        } catch (e) {
            alert('Error al subir el archivo');
        }
        setUploading(false);
    };

    // Eliminar archivo seleccionado antes de enviar
    const handleRemoveSelectedFile = () => {
        setSelectedFile(null);
        alertaGeneral({
            titulo: 'Archivo eliminado',
            messageHtml: '',
        });
    };

    return (
        <>
            {allAppointments && allAppointments.sort((a, b) => { return ordenEstados[a.estado] - ordenEstados[b.estado] }).map((appointment, index) => (
            <div
                className="flex-shrink-0 w-full sm:w-72 flex flex-col justify-between rounded-2xl shadow-[0_12px_32px_-8px_rgba(31,148,194,0.35)] hover:shadow-[0_20px_40px_-10px_rgba(31,148,194,0.4)] transition-shadow duration-300 border border-white/10 bg-gradient-to-br from-[#1f94c2ff] to-[#1a7fa8] p-4 sm:p-5 min-h-0"
                key={index}
            >
                {/* Contenedor móvil: foto + datos en fila para ahorrar espacio */}
                <div className="flex flex-row sm:flex-col sm:text-center gap-4 sm:gap-0">
                    {/* Foto del doctor/paciente */}
                    <div className="flex-shrink-0 w-20 h-20 sm:mx-auto sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-xl sm:rounded-2xl overflow-hidden border-2 sm:border-4 border-white shadow-lg bg-white flex items-center justify-center ring-2 ring-[#1f94c2]/20">
                        {isPatient ? (
                            appointment.doctor?.urlProfessionalPhoto ? (
                                <img
                                    src={appointment.doctor.urlProfessionalPhoto}
                                    alt="foto perfil doctor"
                                    className="w-full h-full object-cover"
                                    referrerPolicy="no-referrer"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null
                        ) : (
                            appointment.patient?.urlPictureProfile ? (
                                <img
                                    src={appointment.patient.urlPictureProfile}
                                    alt="foto perfil paciente"
                                    className="w-full h-full object-cover"
                                    referrerPolicy="no-referrer"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : null
                        )}
                        <div className="w-full h-full flex items-center justify-center bg-slate-100" style={{ display: appointment.doctor?.urlProfessionalPhoto || appointment.patient?.urlPictureProfile ? 'none' : 'flex' }}>
                            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                        </div>
                    </div>

                    {/* Información */}
                    <div className="flex-1 min-w-0 text-left sm:text-center">
                        <h3 className="text-white font-bold text-[15px] sm:text-base leading-tight line-clamp-2">
                            {isPatient
                                ? (appointment.doctor?.fullName || appointment.doctorName || 'Doctor no disponible')
                                : (appointment.patient?.fullName || appointment.patientName || 'Paciente no disponible')
                            }
                        </h3>
                        <p className="text-white/95 text-xs sm:text-sm mt-0.5 line-clamp-1">{formatServiceName(appointment.service)}</p>
                        <p className="text-white/90 text-xs sm:text-sm mt-1.5 sm:mt-1">
                            <span className="sm:hidden">{appointment.date.format("DD MMM")}</span>
                            <span className="hidden sm:inline">{appointment.date.format("DD [de] MMMM YYYY")}</span>
                        </p>
                        <p className="text-white/90 text-xs sm:text-sm capitalize font-medium">
                            {appointment.date.format("dddd")} · {appointment.date.format("hh:mm A")}
                        </p>
                        <span className={`mt-2 sm:mt-3 inline-block ${colorEstado(appointment.estado)} text-white text-xs sm:text-sm font-medium px-3 py-1 sm:px-4 sm:py-1.5 rounded-full capitalize`}>
                            {appointment.estado}
                        </span>
                    </div>
                </div>

                {/* Acciones: botones táctiles en móvil */}
                {appointment.estado === 'Agendada' && (
                    <div className="space-y-2 mt-4 sm:mt-4">
                        <button
                            onClick={() => handleVideoCall(appointment._id, appointment.firmoConsentimiento, appointment.isPatientProfileComplete, appointment.date)}
                            className="w-full flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0 py-3 sm:py-2.5 px-4 bg-white text-[#1f94c2ff] font-semibold rounded-xl hover:bg-white/95 active:scale-[0.98] transition-all shadow-md text-sm sm:text-base"
                        >
                            <SvgIcon Icon={Teleconsulta} className="w-5 h-5 shrink-0" />
                            <span>Unirse a la videoconsulta</span>
                        </button>
                        {appointment.firmoConsentimiento === 'Si' && (
                            <button
                                onClick={() => handleOpenModal(appointment._id)}
                                className="w-full flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0 py-3 sm:py-2.5 px-4 bg-white/20 text-white font-medium rounded-xl hover:bg-white/30 transition-colors border border-white/30 text-sm sm:text-base"
                            >
                                <Archivo className="w-5 h-5 shrink-0" />
                                <span>Agregar documentos</span>
                            </button>
                        )}
                        <button
                            onClick={() => handleCancel(appointment._id)}
                            className="w-full flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0 py-3 sm:py-2.5 px-4 bg-red-500/90 hover:bg-red-500 text-white font-medium rounded-xl transition-colors text-sm sm:text-base"
                        >
                            <FontAwesomeIcon icon={faTimesCircle} className="w-5 h-5 shrink-0" />
                            <span>Cancelar cita</span>
                        </button>
                    </div>
                )}
            </div>
            ))}
            {modalOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative flex flex-col border border-slate-100">
                        <button className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 text-2xl leading-none" onClick={() => setModalOpen(false)} aria-label="Cerrar">&times;</button>
                        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center pr-8"><Archivo className="w-6 h-6 mr-2 text-[#1f94c2ff]" /> Documentos de la cita</h2>
                        <div className="w-full space-y-4">
                            <div className="text-slate-600 text-sm space-y-2">
                              <p className="font-medium text-slate-700">Cargar documentos para revisión del médico (opcional)</p>
                              <p>Puedes adjuntar historias clínicas previas, resultados de laboratorio, imágenes diagnósticas, fórmulas médicas u otros documentos clínicos relevantes.</p>
                              <p className="text-xs text-slate-500">Formatos: PDF, JPG, PNG, Word (.doc, .docx)</p>
                            </div>
                            <InputFile fileName={selectedFile ? selectedFile.name : "Agregar un archivo"} onChange={e => setSelectedFile(e.target.files[0])} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png" />
                            <div className="rounded-xl border border-slate-200 overflow-hidden">
                                <table className="w-full bg-white">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="text-left px-4 py-3 text-slate-600 font-medium text-sm">Archivo</th>
                                            <th className="text-right px-4 py-3 text-slate-600 font-medium text-sm">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedFile && (
                                            <tr className="border-b border-slate-100">
                                                <td className="px-4 py-2.5 text-sm text-slate-700">{selectedFile.name}</td>
                                                <td className="px-4 py-2.5 text-right">
                                                    <button onClick={handleRemoveSelectedFile} className="text-red-500 hover:text-red-600 font-medium text-sm">Eliminar</button>
                                                </td>
                                            </tr>
                                        )}
                                        {documents.map((file, index) => (
                                            <tr key={index} className="border-b border-slate-100 last:border-0">
                                                <td className="px-4 py-2.5 text-sm text-slate-700">{file.name}</td>
                                                <td className="px-4 py-2.5 text-right">
                                                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-[#1f94c2ff] hover:underline font-medium text-sm">Ver</a>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button
                                onClick={handleSendFile}
                                className="w-full py-2.5 px-4 bg-[#1f94c2ff] hover:bg-[#1a7fa8] text-white font-medium rounded-xl disabled:opacity-50 transition-colors"
                                disabled={!selectedFile || uploading}
                            >
                                {uploading ? 'Enviando...' : 'Enviar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CardCitasControl;